/* Appsalon: Minimum cart amount checkout guard
   Keeps the checkout button blocked if another cart script (for example the
   terms checkbox) tries to enable it while the cart is below the theme setting.
*/
(function () {
  'use strict';

  let changeListenerBound = false;

  function getBlockedMinimumCartMessage() {
    return document.querySelector('[data-appsalon-min-cart][data-min-cart-blocked="true"]');
  }

  function isMinimumCartBlocked() {
    return !!getBlockedMinimumCartMessage();
  }

  function enforceMinimumCartButtons() {
    document.querySelectorAll('#CheckOut[data-appsalon-min-cart-disabled="true"]').forEach((button) => {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
    });
  }

  function highlightMinimumCartMessage() {
    const message = getBlockedMinimumCartMessage();
    if (!message) return;

    message.classList.remove('appsalon-min-cart--attention');
    // Force a reflow so the animation can replay on repeated attempts.
    void message.offsetWidth;
    message.classList.add('appsalon-min-cart--attention');

    if (typeof message.scrollIntoView === 'function') {
      message.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }

  function isCheckoutFormSubmit(form, event) {
    const submitter = event.submitter || document.activeElement;

    if (submitter && submitter.getAttribute && submitter.getAttribute('name') === 'checkout') {
      return true;
    }

    if (form && form.id === 'cart') {
      return true;
    }

    return !!(form && form.querySelector('button[name="checkout"], input[name="checkout"]'));
  }

  function handleSubmit(event) {
    const form = event.target;
    if (!form || form.tagName !== 'FORM') return;
    if (!isMinimumCartBlocked() || !isCheckoutFormSubmit(form, event)) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    enforceMinimumCartButtons();
    highlightMinimumCartMessage();
  }

  function initMinimumCartGuard() {
    enforceMinimumCartButtons();

    if (!changeListenerBound) {
      changeListenerBound = true;
      document.addEventListener('change', (event) => {
        if (event.target && event.target.id === 'AgreeToTos') {
          window.setTimeout(enforceMinimumCartButtons, 0);
        }
      }, true);
    }

    const subtotal = document.getElementById('AjaxCartSubtotal');
    if (subtotal && !subtotal.dataset.appsalonMinCartObserver) {
      subtotal.dataset.appsalonMinCartObserver = 'true';
      new MutationObserver(enforceMinimumCartButtons).observe(subtotal, {
        childList: true,
        subtree: true
      });
    }
  }

  document.addEventListener('submit', handleSubmit, true);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMinimumCartGuard);
  } else {
    initMinimumCartGuard();
  }

  document.addEventListener('shopify:section:load', initMinimumCartGuard);
})();

/* Appsalon: Norwegian bottle deposit (pant)
   - Products with product.metafields.custom.pant_produkt get data-pant-variant-id in Liquid.
   - This script adds the main product and the pant variant in the same Cart Ajax request.
   - Pant is added as a nested cart line using parent_id/parent_line_key, so checkout and orders can show it under the main product.
   - Pant lines are kept in sync with product quantity by using nested line relations and cart DOM data.
   - No technical pant properties are added to cart/order lines, so Shopify Admin stays cleaner.
*/
(function () {
  'use strict';

  const prefix = window.KT_PREFIX || '';

  function routes() {
    return window.KROWN && window.KROWN.settings && window.KROWN.settings.routes
      ? window.KROWN.settings.routes
      : null;
  }

  function dispatchKrownEvent(eventType, detail) {
    if (window.KROWN && window.KROWN.functions && typeof window.KROWN.functions.eventDispatcher === 'function') {
      window.KROWN.functions.eventDispatcher(eventType, detail);
    }
  }

  function getCartType(productForm) {
    if (!productForm.hasAttribute('data-ajax-cart')) return 'page';
    return window.KROWN && window.KROWN.settings && window.KROWN.settings.cart_action === 'popup'
      ? 'popup'
      : 'ajax';
  }

  function parseQuantity(value) {
    const quantity = parseInt(value || '1', 10);
    return Number.isNaN(quantity) || quantity < 1 ? 1 : quantity;
  }

  function getLineProperties(item) {
    return item && item.properties ? item.properties : {};
  }

  function isOldPantPropertyLine(item) {
    const properties = getLineProperties(item);
    return properties._pant_charge === 'true';
  }

  function getParentKey(item) {
    if (!item || !item.parent_relationship) return null;
    return item.parent_relationship.parent_key || null;
  }

  function getPantVariantIdFromCartDom(parentLineKey) {
    let pantVariantId = null;

    document.querySelectorAll('[data-js-cart-item][data-id][data-pant-variant-id]').forEach((element) => {
      if (pantVariantId) return;
      if (element.dataset.id === parentLineKey && element.dataset.pantVariantId) {
        pantVariantId = String(element.dataset.pantVariantId);
      }
    });

    return pantVariantId;
  }

  function buildPantAddPayload(form, pantVariantId) {
    const formData = new FormData(form);
    const productVariantId = Number(formData.get('id'));
    const quantity = parseQuantity(formData.get('quantity'));
    const properties = {};

    formData.forEach((value, name) => {
      const propertyMatch = name.match(/^properties\[(.*)\]$/);
      if (propertyMatch && value !== '') {
        properties[propertyMatch[1]] = value;
      }
    });

    const productItem = {
      id: productVariantId,
      quantity: quantity,
      properties: properties
    };

    const sellingPlan = formData.get('selling_plan');
    if (sellingPlan) {
      productItem.selling_plan = Number(sellingPlan);
    }

    return JSON.stringify({
      items: [
        productItem,
        {
          id: Number(pantVariantId),
          quantity: quantity,
          parent_id: productVariantId
        }
      ]
    });
  }

  function validateRequiredCustomProperties(productForm) {
    let customPropertiesRequired = false;
    const productPage = productForm.closest('[data-js-product-component]');

    if (!productPage) return false;

    productPage.querySelectorAll('[data-js-custom-property]').forEach((elm) => {
      if (!elm.querySelector('[required]')) return;

      if (elm.classList.contains(`${prefix}product-variants--text`)) {
        const txtInputElm = elm.querySelector('input') || elm.querySelector('textarea');
        if (txtInputElm && txtInputElm.value === '') {
          customPropertiesRequired = true;
          txtInputElm.focus();
          const requiredText = elm.querySelector(`.${prefix}product-variant__required`);
          if (requiredText) requiredText.style.display = 'block';
        }
      } else if (elm.classList.contains(`${prefix}product-variants--select`)) {
        const selectElm = elm.querySelector('select');
        if (selectElm && selectElm.value === '') {
          customPropertiesRequired = true;
          selectElm.focus();
          const requiredText = elm.querySelector(`.${prefix}product-variant__required`);
          if (requiredText) requiredText.style.display = 'block';
        }
      } else if (elm.classList.contains(`${prefix}product-variants--checkbox`)) {
        if (!elm.querySelector('input:checked')) {
          customPropertiesRequired = true;
          const inputElm = elm.querySelector('input');
          if (inputElm) inputElm.focus();
          const requiredText = elm.querySelector(`.${prefix}product-variant__required`);
          if (requiredText) requiredText.style.display = 'block';
        }
      }

      elm.addEventListener('click', () => {
        const requiredText = elm.querySelector(`.${prefix}product-variant__required`);
        if (requiredText) requiredText.style.display = 'none';
      });
    });

    return customPropertiesRequired;
  }

  function renderProductFormError(productForm, form, cartType, response) {
    const alert = document.createElement('span');
    alert.className = `${prefix}alert ${prefix}alert--error`;

    if (typeof response.description === 'string') {
      alert.innerHTML = response.description;
    } else if (response.errors && response.errors.send_on) {
      alert.innerHTML = response.errors.send_on;
    } else {
      alert.innerHTML = response.message || (window.KROWN && window.KROWN.settings && window.KROWN.settings.locales ? window.KROWN.settings.locales.cart_general_error : 'Could not add item to cart.');
    }

    if (cartType === 'page' || cartType === 'popup') {
      const existingAlert = document.getElementById('product-page-form-error-cart-alert');
      if (existingAlert) existingAlert.remove();
      alert.style.marginTop = '1em';
      alert.style.marginBottom = '0';
      alert.id = 'product-page-form-error-cart-alert';
      form.parentElement.append(alert);
    } else if (document.getElementById('AjaxCartForm')) {
      document.getElementById('AjaxCartForm').querySelector('form').prepend(alert);
    } else {
      form.parentElement.append(alert);
    }

    dispatchKrownEvent('krown:cart:error', { error: alert.textContent });
  }

  function updateCartUiFromHelper(text, productForm, cartType, alert) {
    const sectionInnerHTML = new DOMParser().parseFromString(text, 'text/html');

    if (cartType === 'ajax') {
      const helperCartForm = sectionInnerHTML.getElementById('AjaxCartForm');
      const helperCartSubtotal = sectionInnerHTML.getElementById('AjaxCartSubtotal');

      if (helperCartForm && document.getElementById('AjaxCartForm')) {
        const cartItems = document.getElementById('AjaxCartForm');
        cartItems.innerHTML = helperCartForm.innerHTML;
        if (typeof cartItems.ajaxifyCartItems === 'function') {
          cartItems.ajaxifyCartItems();
        }
        if (alert) {
          const cartForm = document.getElementById('AjaxCartForm').querySelector('form');
          if (cartForm) cartForm.prepend(alert);
        }
      }

      if (helperCartSubtotal && document.getElementById('AjaxCartSubtotal')) {
        document.getElementById('AjaxCartSubtotal').innerHTML = helperCartSubtotal.innerHTML;
      }
    } else if (cartType === 'popup') {
      const popupContent = document.querySelector('[data-js-site-cart-popup-content]');
      const shopifySection = sectionInnerHTML.querySelector('.shopify-section');
      if (popupContent && shopifySection) {
        popupContent.innerHTML = shopifySection.innerHTML;
      }
    }

    const helperCartCount = sectionInnerHTML.querySelector('[data-cart-count]');
    if (helperCartCount) {
      document.querySelectorAll('[data-header-cart-count]').forEach((elm) => {
        elm.textContent = helperCartCount.textContent;
      });
    }

    const helperCartTotal = sectionInnerHTML.querySelector('[data-cart-total]');
    if (helperCartTotal) {
      document.querySelectorAll('[data-header-cart-total]').forEach((elm) => {
        elm.textContent = helperCartTotal.textContent;
      });
    }

    productForm.dispatchEvent(new Event('add-to-cart'));
    dispatchKrownEvent('krown:cart:add', {
      productForm: productForm.querySelector('form'),
      variantId: productForm.querySelector('input[name="id"]') ? productForm.querySelector('input[name="id"]').value : null
    });

    const screenReaderInfo = document.getElementById('screen-reader-info');
    if (screenReaderInfo && window.KROWN && window.KROWN.settings && window.KROWN.settings.locales) {
      screenReaderInfo.innerText = `${window.KROWN.settings.locales.cart_announcement}`;
      setTimeout(() => {
        screenReaderInfo.innerText = '';
      }, 1000);
    }
  }

  function handlePantProductSubmit(event) {
    const form = event.target;
    if (!form || form.tagName !== 'FORM') return;

    const productForm = form.closest('product-form[data-pant-variant-id]');
    if (!productForm) return;

    const pantVariantId = productForm.dataset.pantVariantId;
    if (!pantVariantId || Number.isNaN(Number(pantVariantId))) return;

    const routeSettings = routes();
    if (!routeSettings) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const submitButton = event.submitter || productForm.querySelector('[type="submit"]');

    if (productForm.classList.contains(`${prefix}inventory-threshold-blocked`) || submitButton?.hasAttribute('hidden')) {
      return false;
    }

    if (submitButton) submitButton.classList.add(`${prefix}working`);

    if (validateRequiredCustomProperties(productForm)) {
      if (submitButton) submitButton.classList.remove(`${prefix}working`);
      return false;
    }

    const cartType = getCartType(productForm);
    const body = buildPantAddPayload(form, pantVariantId);
    let alert = '';

    fetch(`${routeSettings.cart_add_url}.js`, {
      body: body,
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      method: 'POST'
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status === 422) {
          renderProductFormError(productForm, form, cartType, response);
          return false;
        }

        if (cartType === 'page') {
          document.location.href = routeSettings.cart_url;
          return false;
        }

        if (cartType === 'popup') {
          const variantInput = productForm.querySelector('input[name="id"]');
          const variantQuery = variantInput ? `?variant=${variantInput.value}&section_id=helper-cart-popup` : '?section_id=helper-cart-popup';
          return fetch(`${productForm.dataset.productUrl}${variantQuery}`);
        }

        return fetch('?section_id=helper-cart');
      })
      .then((response) => {
        if (!response || response === false) return false;
        return response.text();
      })
      .then((text) => {
        if (!text) return;
        updateCartUiFromHelper(text, productForm, cartType, alert);
      })
      .catch((e) => {
        console.log(e);
      })
      .finally(() => {
        if (submitButton) submitButton.classList.remove(`${prefix}working`);
      });

    return false;
  }

  function cartChange(routeSettings, line, quantity) {
    return fetch(routeSettings.cart_change_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
      body: JSON.stringify({
        line: line,
        quantity: quantity
      })
    });
  }

  function addPantLine(routeSettings, pantVariantId, quantity, parentLineKey) {
    return fetch(`${routeSettings.cart_add_url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
      body: JSON.stringify({
        items: [
          {
            id: Number(pantVariantId),
            quantity: quantity,
            parent_line_key: parentLineKey
          }
        ]
      })
    });
  }

  function syncPantLines() {
    const routeSettings = routes();
    if (!routeSettings) return Promise.resolve(false);

    return fetch(`${routeSettings.cart_url}.js`)
      .then((response) => response.json())
      .then((cart) => {
        const expectedParents = [];
        const expectedParentKeys = {};
        const expectedPantByParentKey = {};
        const pantLines = [];

        cart.items.forEach((item, index) => {
          const properties = getLineProperties(item);
          const lineNumber = index + 1;
          const parentKey = getParentKey(item);

          if (parentKey || isOldPantPropertyLine(item)) {
            pantLines.push({
              item: item,
              line: lineNumber,
              variantId: String(item.variant_id),
              parentKey: parentKey,
              oldPropertyLine: isOldPantPropertyLine(item)
            });
            return;
          }

          const pantVariantId = getPantVariantIdFromCartDom(item.key) || properties._pant_variant_id;

          if (pantVariantId) {
            expectedParentKeys[item.key] = true;
            expectedPantByParentKey[item.key] = String(pantVariantId);
            expectedParents.push({
              parentKey: item.key,
              pantVariantId: String(pantVariantId),
              quantity: item.quantity
            });
          }
        });

        const matchedPantLineIndexes = {};
        const quantityChanges = [];
        const removals = [];
        const additions = [];

        expectedParents.forEach((expected) => {
          const matches = pantLines
            .map((line, index) => ({ line, index }))
            .filter((entry) => {
              return entry.line.variantId === expected.pantVariantId && entry.line.parentKey === expected.parentKey;
            });

          if (matches.length === 0 && expected.quantity > 0) {
            additions.push(expected);
            return;
          }

          matches.forEach((entry, matchIndex) => {
            matchedPantLineIndexes[entry.index] = true;
            const targetQty = matchIndex === 0 ? expected.quantity : 0;
            if (entry.line.item.quantity !== targetQty) {
              if (targetQty === 0) {
                removals.push({ line: entry.line.line, quantity: 0 });
              } else {
                quantityChanges.push({ line: entry.line.line, quantity: targetQty });
              }
            }
          });
        });

        pantLines.forEach((line, index) => {
          if (matchedPantLineIndexes[index]) return;

          const expectedPantVariantId = line.parentKey ? expectedPantByParentKey[line.parentKey] : null;
          const isStalePantForKnownParent = expectedPantVariantId && line.variantId === expectedPantVariantId;

          // Remove old property-based pant lines from earlier theme versions, plus duplicate/stale pant child lines.
          if (line.oldPropertyLine || isStalePantForKnownParent) {
            removals.push({ line: line.line, quantity: 0 });
          }
        });

        const runSequentially = (tasks) => {
          return tasks.reduce((promise, task) => promise.then(task), Promise.resolve());
        };

        const tasks = [];

        // Quantity changes first, so line numbers are still stable.
        quantityChanges.forEach((change) => {
          tasks.push(() => cartChange(routeSettings, change.line, change.quantity));
        });

        // Removals are performed from the bottom up, so line numbers do not shift before later removals.
        removals
          .sort((a, b) => b.line - a.line)
          .forEach((change) => {
            tasks.push(() => cartChange(routeSettings, change.line, 0));
          });

        additions.forEach((addition) => {
          tasks.push(() => addPantLine(routeSettings, addition.pantVariantId, addition.quantity, addition.parentKey));
        });

        if (tasks.length === 0) return false;
        return runSequentially(tasks).then(() => true);
      });
  }

  let syncingAfterCartUpdate = false;

  function refreshCartAfterPantSync() {
    if (syncingAfterCartUpdate) return;
    syncingAfterCartUpdate = true;

    syncPantLines()
      .then((changed) => {
        if (changed && typeof window.refreshCart === 'function') {
          window.refreshCart(false);
        }
      })
      .catch((e) => console.log(e))
      .finally(() => {
        setTimeout(() => {
          syncingAfterCartUpdate = false;
        }, 0);
      });
  }

  function handleCheckoutSubmit(event) {
    const form = event.target;
    if (!form || form.tagName !== 'FORM' || form.dataset.pantCheckoutSynced === 'true') return;

    const submitter = event.submitter || document.activeElement;
    const isCheckoutSubmit = submitter && submitter.getAttribute && submitter.getAttribute('name') === 'checkout';
    const hasCheckoutInput = form.querySelector('button[name="checkout"], input[name="checkout"]');

    if (!isCheckoutSubmit && !hasCheckoutInput) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    form.classList.add(`${prefix}processing`);

    syncPantLines()
      .catch((e) => console.log(e))
      .finally(() => {
        form.dataset.pantCheckoutSynced = 'true';

        if (!form.querySelector('input[name="checkout"]')) {
          const checkoutInput = document.createElement('input');
          checkoutInput.type = 'hidden';
          checkoutInput.name = 'checkout';
          checkoutInput.value = submitter && submitter.value ? submitter.value : 'checkout';
          form.append(checkoutInput);
        }

        form.submit();
      });
  }

  document.addEventListener('submit', handlePantProductSubmit, true);
  document.addEventListener('submit', handleCheckoutSubmit, true);
  document.addEventListener('cart-updated', refreshCartAfterPantSync, true);

  window.AppsalonPant = window.AppsalonPant || {};
  window.AppsalonPant.syncPantLines = syncPantLines;
})();

/* Appsalon: Constant sticky header
   Keeps Local theme's existing sticky clone visible on scroll down when
   header.liquid has data-sticky-header-always. Does not edit section-header.js.
*/
(function () {
  'use strict';

  let stickyScrollBound = false;
  let ticking = false;
  let observer = null;

  function getHeader() {
    return document.getElementById('site-header');
  }

  function getStickyHeader() {
    return document.querySelector('.sticky-header');
  }

  function isAlwaysStickyEnabled(header) {
    return !!(header && header.hasAttribute('data-sticky-header-always'));
  }

  function getStickyTriggerElement(header) {
    if (!header) return null;
    return window.innerWidth >= 1024
      ? header.querySelector('.header__bottom')
      : header.querySelector('.header__top');
  }

  function shouldShowStickyHeader(header) {
    const triggerElement = getStickyTriggerElement(header);
    if (!triggerElement) return window.scrollY > 0;

    return window.scrollY > 0 && triggerElement.getBoundingClientRect().top <= 0;
  }

  function updateStickyHeader() {
    ticking = false;

    const header = getHeader();
    const stickyHeader = getStickyHeader();

    if (!isAlwaysStickyEnabled(header) || !stickyHeader) return;

    stickyHeader.classList.toggle('show', shouldShowStickyHeader(header));
  }

  function requestStickyUpdate() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(updateStickyHeader);
  }

  function replaceThemeStickyScrollHandler(header) {
    if (!header || header.dataset.appsalonStickyAlwaysHandler === 'true') return;

    if (typeof header.SCROLL_StickyHelper === 'function') {
      window.removeEventListener('scroll', header.SCROLL_StickyHelper);
    }

    header.dataset.appsalonStickyAlwaysHandler = 'true';
  }

  function bindAlwaysStickyHeader() {
    const header = getHeader();
    const stickyHeader = getStickyHeader();

    if (!isAlwaysStickyEnabled(header) || !stickyHeader) return;

    replaceThemeStickyScrollHandler(header);

    if (!stickyScrollBound) {
      stickyScrollBound = true;
      window.addEventListener('scroll', requestStickyUpdate, { passive: true });
      window.addEventListener('resize', requestStickyUpdate);
      window.addEventListener('orientationchange', requestStickyUpdate);
    }

    requestStickyUpdate();
  }

  function observeStickyHeaderCreation() {
    if (observer || !document.documentElement) return;

    observer = new MutationObserver(bindAlwaysStickyHeader);
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  }

  function initAlwaysStickyHeader() {
    bindAlwaysStickyHeader();
    observeStickyHeaderCreation();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAlwaysStickyHeader);
  } else {
    initAlwaysStickyHeader();
  }

  document.addEventListener('shopify:section:load', function () {
    window.setTimeout(initAlwaysStickyHeader, 0);
  });
})();




/* Custom collection availability filter
   Shows in-stock products by default, while allowing customers to show all.
*/
(function () {
  'use strict';

  if (!document.body.classList.contains('template-collection')) return;

  const filterName = 'filter.v.availability';
  const url = new URL(window.location.href);
  const storageKey = `appsalon-show-all:${url.pathname}`;

  /*
   * The availability filter is currently active.
   * Remember that it has been applied to this collection.
   */
  if (url.searchParams.has(filterName)) {
    sessionStorage.setItem(storageKey, 'filter-applied');
    return;
  }

  /*
   * If the filter was previously applied but is now missing,
   * the customer has chosen to show all products.
   */
  if (sessionStorage.getItem(storageKey) === 'filter-applied') {
    sessionStorage.setItem(storageKey, 'show-all');
    return;
  }

  /*
   * Do not reapply the filter after the customer has chosen
   * to show both available and sold-out products.
   */
  if (sessionStorage.getItem(storageKey) === 'show-all') {
    return;
  }

  // Preserve existing filters and add the in-stock filter.
  url.searchParams.set(filterName, '1');
  sessionStorage.setItem(storageKey, 'filter-applied');
  window.location.replace(url.toString());
})();

/* Åpne eksterne linker i nytt vindu */ 
var links = document.links;
for (let i = 0, linksLength = links.length ; i < linksLength ; i++) {
  if (links[i].hostname !== window.location.hostname) {
    links[i].target = '_blank';
    links[i].relList.add('noreferrer', 'noopener');
  }
}